import { useState } from "react";
import {
  Box,
  Text,
  IconButton,
} from "@radix-ui/themes";
import { Trash2, ArrowUpToLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Photo } from "@/lib/api";
import { resolveAssetUrl, getToken } from "@/lib/api";
import { PromoteDialog } from "@/components/shared/promote-dialog";

interface PhotoCardProps {
  photo: Photo;
  onDelete: (photoId: string) => void;
  onPromote: (photoId: string) => void;
  isDeleting?: boolean;
  isPromoting?: boolean;
}

export function PhotoCard({ photo, onDelete, onPromote, isDeleting, isPromoting }: PhotoCardProps) {
  const { t } = useTranslation();
  const [promoteOpen, setPromoteOpen] = useState(false);

  function handlePromoteConfirm() {
    onPromote(photo.id);
    setPromoteOpen(false);
  }

  return (
    <Box>
      <Box style={{ position: "relative", borderRadius: "var(--radius-3)", overflow: "hidden" }}>
        <img
          src={resolveAssetUrl(photo.image_url, getToken())}
          alt={photo.caption || "Photo"}
          style={{
            width: "100%",
            height: "160px",
            objectFit: "cover",
            display: "block",
            cursor: "pointer",
          }}
          onClick={() => setPromoteOpen(true)}
          title={t("board.photo_promote_hint")}
        />

        {/* Bouton supprimer */}
        <Box style={{ position: "absolute", top: "var(--space-1)", right: "var(--space-1)" }}>
          <IconButton
            size="1"
            color="red"
            variant="solid"
            onClick={() => onDelete(photo.id)}
            disabled={isDeleting}
            aria-label={t("board.delete_photo_aria")}
          >
            <Trash2 size={12} />
          </IconButton>
        </Box>

        {/* Bouton promouvoir */}
        <Box style={{ position: "absolute", top: "var(--space-1)", left: "var(--space-1)" }}>
          <IconButton
            size="1"
            color="violet"
            variant="solid"
            onClick={() => setPromoteOpen(true)}
            disabled={isPromoting}
            aria-label={t("board.photo_promote_aria")}
          >
            <ArrowUpToLine size={12} />
          </IconButton>
        </Box>

        {photo.caption && (
          <Box
            px="2"
            py="1"
            style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
            }}
          >
            <Text size="1" style={{ color: "white" }} truncate>
              {photo.caption}
            </Text>
          </Box>
        )}
      </Box>

      <Text size="1" color="gray" mt="1">
        {photo.author_name} ·{" "}
        {new Date(photo.created).toLocaleString("fr-FR", {
          dateStyle: "short",
          timeStyle: "short",
        })}
      </Text>

      {/* Dialog de confirmation */}
      <PromoteDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        onConfirm={handlePromoteConfirm}
        isPending={isPromoting}
        title={t("board.photo_promote_title")}
        confirm={t("board.photo_promote_confirm")}
        action={t("board.photo_promote_action")}
      />
    </Box>
  );
}
